import { BookOpen, CircleHelp, PencilLine } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { Popover } from '@/components/ui/Popover'
import { navLabel, type NavItem } from '@/config/navigation'
import { docsKeyOf, loadDocs } from '@/features/docs/content'
import { DocContent, docsPath, useBlocks } from '@/features/docs/DocContent'
import type { DocSections } from '@/features/docs/markdown'
import { useI18n, useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { readJSON, writeJSON } from '@/lib/storage'

type HelpContextValue = {
  item: NavItem
  /** The one page-level «?» that shows: the first header that asked for it. */
  primary: string | null
  claim: (id: string) => void
  release: (id: string) => void
  /** Openers of form «?» popovers: the «?» key opens the newest form's, else the page docs. */
  setFormOpener: (id: string, open: (() => void) | null) => void
  openDocs: () => void
  seen: boolean
  markSeen: () => void
}

const HelpContext = createContext<HelpContextValue | null>(null)

const SEEN_KEY = 'mf.help.seen'
/** How long a page may go without a header «?» before the floating one shows (lazy pages mount late). */
const FALLBACK_DELAY = 600

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))

/** Where a page's docs live, remembering the page so the docs can lead back to it. */
export function useDocsLink(item: NavItem) {
  const location = useLocation()
  return { to: docsPath(docsKeyOf(item)), state: { from: `${location.pathname}${location.search}` } }
}

/**
 * Gives every routed page its «?»: headers (ListHeader, PageHeader) render it
 * next to the title and it opens the page's docs (/docs/<pageKey>). A page
 * without such a header still gets a floating one, so no page is left without
 * docs. «?» on the keyboard opens them too; inside a form it opens the form's tips.
 */
export function PageHelpProvider({ item, children }: { item: NavItem; children: ReactNode }) {
  const [claims, setClaims] = useState<string[]>([])
  const formOpeners = useRef(new Map<string, () => void>())
  const [seenIds, setSeenIds] = useState(() => readJSON<string[]>(SEEN_KEY, []))
  // Set once the page has gone FALLBACK_DELAY without a header «?»; shown only while none is claimed.
  const [waited, setWaited] = useState(false)
  const navigate = useNavigate()
  const { to: docsTo, state: { from } } = useDocsLink(item)

  const claim = useCallback((id: string) => setClaims((current) => (current.includes(id) ? current : [...current, id])), [])
  const release = useCallback((id: string) => setClaims((current) => current.filter((other) => other !== id)), [])
  const setFormOpener = useCallback((id: string, open: (() => void) | null) => {
    formOpeners.current.delete(id)
    if (open) formOpeners.current.set(id, open)
  }, [])
  const markSeen = useCallback(() => {
    setSeenIds((current) => {
      if (current.includes(item.id)) return current
      const next = [...current, item.id]
      writeJSON(SEEN_KEY, next)
      return next
    })
  }, [item.id])
  const openDocs = useCallback(() => {
    markSeen()
    navigate(docsTo, { state: { from } })
  }, [markSeen, navigate, docsTo, from])

  useEffect(() => {
    if (item.isDocs || claims.length || waited) return
    const timer = window.setTimeout(() => setWaited(true), FALLBACK_DELAY)
    return () => window.clearTimeout(timer)
  }, [item.isDocs, claims.length, waited])
  const fallback = !item.isDocs && waited && claims.length === 0

  useEffect(() => {
    if (item.isDocs) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '?' || event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target)) return
      event.preventDefault()
      const form = [...formOpeners.current.values()].at(-1)
      if (form) form()
      else openDocs()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [item.isDocs, openDocs])

  const value = useMemo<HelpContextValue>(
    () => ({ item, primary: claims[0] ?? null, claim, release, setFormOpener, openDocs, seen: seenIds.includes(item.id), markSeen }),
    [item, claims, claim, release, setFormOpener, openDocs, seenIds, markSeen],
  )

  return (
    <HelpContext.Provider value={value}>
      {children}
      {fallback && (
        <div className="pointer-events-none fixed bottom-4 right-4 z-40 sm:bottom-5 sm:right-5">
          <div className="pointer-events-auto">
            <PageDocsButton floating />
          </div>
        </div>
      )}
    </HelpContext.Provider>
  )
}

/** The page's «?» in a header, right after the title. Only the first one on a page shows. */
export function PageHelp({ className }: { className?: string }) {
  const context = useContext(HelpContext)
  const id = useId()
  const { claim, release } = context ?? {}
  useEffect(() => {
    if (!claim || !release) return
    claim(id)
    return () => release(id)
  }, [claim, release, id])
  if (!context || context.item.isDocs || context.primary !== id) return null
  return <PageDocsButton className={className} />
}

const buttonClass = 'relative inline-flex shrink-0 items-center justify-center rounded-full text-muted transition-colors duration-[var(--dur-base)] hover:bg-hover hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-soft)]'

function PageDocsButton({ floating, className }: { floating?: boolean; className?: string }) {
  const context = useContext(HelpContext)!
  const t = useT()
  const link = useDocsLink(context.item)
  const label = t('layout.help.button')
  return (
    <Link
      to={link.to}
      state={link.state}
      onClick={context.markSeen}
      aria-label={label}
      title={label}
      onPointerEnter={() => void import('@/features/docs/DocsPage').catch(() => undefined)}
      className={cn(buttonClass, floating ? 'glass-menu size-10 text-fg-2 shadow-lg' : 'size-6.5', className)}
    >
      <CircleHelp className={floating ? 'size-5' : 'size-4'} strokeWidth={1.9} />
      {!context.seen && <span aria-hidden className="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-accent" />}
    </Link>
  )
}

/**
 * The «?» of a create / edit form: its steps (the docs' form part) in a
 * popover, so the form stays open, with a link to the full docs in a new tab.
 * `standsForPage` — a form opened as its own page, whose header has no page
 * «?» — keeps the floating one away.
 */
export function FormHelp({ className, standsForPage }: { className?: string; standsForPage?: boolean }) {
  const context = useContext(HelpContext)
  const id = useId()
  const { claim, release } = context ?? {}
  useEffect(() => {
    if (!standsForPage || !claim || !release) return
    claim(id)
    return () => release(id)
  }, [standsForPage, claim, release, id])
  if (!context || context.item.isDocs) return null
  return <FormHelpButton className={className} />
}

function useDocs(key: string) {
  const { locale } = useI18n()
  const [state, setState] = useState<{ key: string; docs: DocSections | null; status: 'ready' | 'failed' } | null>(null)
  const stateKey = `${locale}:${key}`
  const load = useCallback(() => {
    loadDocs(key, locale)
      .then((docs) => setState({ key: stateKey, docs, status: 'ready' }))
      .catch(() => setState({ key: stateKey, docs: null, status: 'failed' }))
  }, [locale, key, stateKey])
  const current = state?.key === stateKey ? state : null
  return { docs: current?.docs ?? null, status: current?.status ?? ('loading' as const), load }
}

function FormHelpButton({ className }: { className?: string }) {
  const context = useContext(HelpContext)!
  const t = useT()
  const id = useId()
  const docs = useDocs(docsKeyOf(context.item))
  const toggleRef = useRef<((forceOpen?: boolean) => void) | null>(null)
  const { setFormOpener } = context

  useEffect(() => {
    setFormOpener(id, () => toggleRef.current?.(true))
    return () => setFormOpener(id, null)
  }, [setFormOpener, id])

  const label = t('layout.help.formButton')
  return (
    <Popover
      kind="dialog"
      align="end"
      className="w-[min(360px,calc(100vw-24px))] p-0"
      trigger={({ open, toggle, ...aria }) => {
        toggleRef.current = (forceOpen) => {
          if (!open) docs.load()
          toggle(forceOpen)
        }
        return (
          <button
            type="button"
            {...aria}
            aria-label={label}
            title={label}
            onPointerEnter={docs.load}
            onFocus={docs.load}
            onClick={() => toggleRef.current?.()}
            className={cn(buttonClass, 'size-6.5', open && 'bg-accent-soft text-accent hover:bg-accent-soft hover:text-accent', className)}
          >
            <CircleHelp className="size-4" strokeWidth={1.9} />
          </button>
        )
      }}
    >
      {() => <FormPanel title={navLabel(t, context.item)} docs={docs} />}
    </Popover>
  )
}

function FormPanel({ title, docs }: { title: string; docs: ReturnType<typeof useDocs> }) {
  const t = useT()
  const context = useContext(HelpContext)!
  const { docs: content, status } = docs
  const blocks = useBlocks(content ? (content.form ?? content.short) : undefined)

  return (
    <div className="flex max-h-[min(520px,calc(100dvh-96px))] flex-col" aria-live="polite">
      <div className="flex items-start gap-2.5 border-b border-line px-4 pb-3 pt-3.5">
        <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
          <PencilLine className="size-3.5" />
        </span>
        <div className="min-w-0">
          <div className="text-[length:calc(11px*var(--text-scale))] font-medium uppercase tracking-wide text-muted">{t('layout.help.formTitle')}</div>
          <div className="truncate text-[length:calc(14px*var(--text-scale))] font-semibold text-fg">{title}</div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-1">
        {status === 'loading' ? (
          <div className="space-y-2 py-2" aria-label={t('layout.help.loading')}>
            <div className="h-3 w-11/12 animate-pulse rounded bg-hover" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-hover" />
            <div className="h-3 w-3/5 animate-pulse rounded bg-hover" />
          </div>
        ) : status === 'failed' ? (
          <p className="py-3 text-[length:calc(13px*var(--text-scale))] text-muted">
            {t('layout.help.failed')}{' '}
            <button type="button" className="font-medium text-accent hover:underline" onClick={docs.load}>
              {t('layout.help.retry')}
            </button>
          </p>
        ) : !content ? (
          <p className="py-3 text-[length:calc(13px*var(--text-scale))] text-muted">{t('layout.help.missing')}</p>
        ) : (
          <DocContent blocks={blocks} className="text-[length:calc(13px*var(--text-scale))] leading-relaxed" />
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-line px-4 py-2 text-[length:calc(12px*var(--text-scale))]">
        <a
          href={docsPath(docsKeyOf(context.item), 'long')}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 font-medium text-accent hover:underline"
        >
          <BookOpen className="size-3.5" />
          {t('layout.help.openDocs')}
        </a>
      </div>
    </div>
  )
}
